import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, 
  Pin, 
  PinOff,
  Trash2, 
  Eye,
  ChevronLeft,
  ChevronRight,
  Image,
  Video,
  FileText,
  Music,
  Loader2,
  Star,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POST_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'mantra', label: 'Mantra' },
  { value: 'audio', label: 'Audio' }
];

const getTypeIcon = (type) => {
  const icons = {
    text: FileText,
    image: Image,
    video: Video,
    audio: Music,
    mantra: Star
  };
  return icons[type] || FileText;
};

export default function AdminPostsPage({ admin }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [postType, setPostType] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    fetchPosts();
  }, [page, postType]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (search) params.append('search', search);
      if (postType) params.append('post_type', postType);

      const response = await axios.get(`${API}/admin/posts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPosts();
  };

  const handleTogglePin = async (postId, currentPinned) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.put(`${API}/admin/posts/${postId}?is_pinned=${!currentPinned}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentPinned ? 'Post unpinned' : 'Post pinned');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleOfficial = async (postId, currentOfficial) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.put(`${API}/admin/posts/${postId}?is_official=${!currentOfficial}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentOfficial ? 'Removed official status' : 'Marked as official');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditPost = async (postId) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.put(`${API}/admin/posts/${postId}?content=${encodeURIComponent(editContent)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post updated');
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to update post');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.delete(`${API}/admin/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setActionLoading(false);
    }
  };

  const getMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/uploads/') || url.startsWith('/uploads/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Post Management</h1>
          <p className="text-slate-500">{total} total posts</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search post content..."
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                data-testid="post-search-input"
              />
            </div>
          </form>

          <select
            value={postType}
            onChange={(e) => { setPostType(e.target.value); setPage(1); }}
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 bg-white"
            data-testid="type-filter"
          >
            {POST_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Posts Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center text-slate-500">
          No posts found
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {posts.map((post) => {
            const TypeIcon = getTypeIcon(post.post_type);
            const mediaUrl = getMediaUrl(post.media_url);
            
            return (
              <div 
                key={post.id} 
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                data-testid={`post-card-${post.id}`}
              >
                {/* Post Header */}
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={post.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user_name)}&background=FF9933&color=fff`}
                      alt={post.user_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-slate-900">{post.user_name}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(post.created_at).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {post.is_pinned && (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">Pinned</span>
                    )}
                    {post.is_official && (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">Official</span>
                    )}
                    <span className={`p-1.5 rounded-lg bg-slate-100`}>
                      <TypeIcon className="w-4 h-4 text-slate-500" />
                    </span>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-4">
                  {editingPost === post.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditPost(post.id)}
                          disabled={actionLoading}
                          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingPost(null)}
                          className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-700 line-clamp-3">{post.content}</p>
                  )}
                </div>

                {/* Media Preview */}
                {mediaUrl && (
                  <div className="px-4 pb-4">
                    {post.media_type === 'video' ? (
                      <video src={mediaUrl} className="w-full h-40 object-cover rounded-lg bg-slate-100" />
                    ) : (
                      <img src={mediaUrl} alt="Post media" className="w-full h-40 object-cover rounded-lg bg-slate-100" />
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                  <div className="text-xs text-slate-500">
                    {post.likes_count || 0} likes
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingPost(post.id);
                        setEditContent(post.content);
                      }}
                      disabled={actionLoading}
                      className="p-2 text-slate-500 hover:bg-slate-200 rounded-lg transition-colors"
                      title="Edit post"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleTogglePin(post.id, post.is_pinned)}
                      disabled={actionLoading}
                      className={`p-2 rounded-lg transition-colors ${
                        post.is_pinned ? 'text-amber-600 hover:bg-amber-50' : 'text-slate-500 hover:bg-slate-200'
                      }`}
                      title={post.is_pinned ? 'Unpin post' : 'Pin post'}
                    >
                      {post.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleToggleOfficial(post.id, post.is_official)}
                      disabled={actionLoading}
                      className={`p-2 rounded-lg transition-colors ${
                        post.is_official ? 'text-orange-600 hover:bg-orange-50' : 'text-slate-500 hover:bg-slate-200'
                      }`}
                      title={post.is_official ? 'Remove official' : 'Mark as official'}
                    >
                      <Star className={`w-4 h-4 ${post.is_official ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      disabled={actionLoading}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete post"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
